import { styled, Theme } from '@mui/material/styles'
import TodayRoundedIcon from '@mui/icons-material/Event'
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded'
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import BeachAccessRoundedIcon from '@mui/icons-material/BeachAccessRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { mobile } from '../../utils/breakpoints'
import { SvgIconProps } from '@mui/material/SvgIcon'
import AddShoppingCartRoundedIcon from '@mui/icons-material/AddShoppingCartRounded'
import RemoveShoppingCartRoundedIcon from '@mui/icons-material/RemoveShoppingCartRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded'
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded'
import Groups2RoundedIcon from '@mui/icons-material/Groups2Rounded'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import AddCardRoundedIcon from '@mui/icons-material/AddCardRounded'
import EditCalendarRoundedIcon from '@mui/icons-material/EditCalendarRounded'
import PriceChangeRoundedIcon from '@mui/icons-material/PriceChangeRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import EmailRoundedIcon from '@mui/icons-material/EmailRounded'
import CurrencyExchangeRoundedIcon from '@mui/icons-material/CurrencyExchangeRounded'
import NumbersRoundedIcon from '@mui/icons-material/NumbersRounded'
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded'
import EuroRoundedIcon from '@mui/icons-material/EuroRounded'
/**
 * Mixin to return the color of the icon, or the default color if the color is not provided
 * @param theme - The theme of the application
 * @param color - The color of the icon
 * @returns The color of the icon, or the default color if the color is not provided
 */
const useColorOrDefault = (theme: Theme, color?: string) => {
  if (!color) {
    return `
      color: ${theme.palette.text.primary};
      .Mui-disabled & {
        color: ${theme.palette.action.disabled};
      }
    `
  }
  return `color: ${color};`
}

/**
 * Mixin to return the font size of the icon, or the default font size if the font size is not provided
 * @param theme - The theme of the application
 * @param fontSize - The font size of the icon
 * @returns The font size of the icon, or the default font size if the font size is not provided
 */
const useFontSizeOrDefault = (theme: Theme, fontSize?: string) => {
  return fontSize
    ? `font-size: ${fontSize};`
    : `
     font-size: 20px;
    ${mobile(theme, { 'font-size': '20px;' })}
  `
}

// Create a configuration array for the icons.
const iconComponents = [
  { name: 'BookingIcon', icon: TodayRoundedIcon },
  { name: 'CancelledBookingsIcon', icon: EventBusyRoundedIcon },
  { name: 'NoBookingsIcon', icon: CalendarTodayRoundedIcon },
  { name: 'InfoIcon', icon: InfoOutlinedIcon },
  { name: 'UmbrellaIcon', icon: BeachAccessRoundedIcon },
  { name: 'CloseIcon', icon: CloseRoundedIcon },
  { name: 'AddCartIcon', icon: AddShoppingCartRoundedIcon },
  { name: 'RemoveCartIcon', icon: RemoveShoppingCartRoundedIcon },
  { name: 'AddIcon', icon: AddRoundedIcon },
  { name: 'RemoveIcon', icon: RemoveRoundedIcon },
  { name: 'AnalyticsIcon', icon: BarChartRoundedIcon },
  { name: 'CustomersIcon', icon: Groups2RoundedIcon },
  { name: 'SettingsIcon', icon: SettingsRoundedIcon },
  { name: 'AddCardIcon', icon: AddCardRoundedIcon },
  { name: 'EditCalendarIcon', icon: EditCalendarRoundedIcon },
  { name: 'PricingIcon', icon: PriceChangeRoundedIcon },
  { name: 'DeleteIcon', icon: DeleteRoundedIcon },
  { name: 'EmailIcon', icon: EmailRoundedIcon },
  { name: 'RefundIcon', icon: CurrencyExchangeRoundedIcon },
  { name: 'NumbersIcon', icon: NumbersRoundedIcon },
  { name: 'PeopleAltIcon', icon: PeopleAltRoundedIcon },
  { name: 'EuroIcon', icon: EuroRoundedIcon },
]

// Create an object to store the styled components.
const styledIcons: { [key: string]: React.ComponentType<SvgIconProps & { customColor?: string; size?: string }> } = {}

// Loop through the configuration and create each styled component.
iconComponents.forEach(({ name, icon }) => {
  styledIcons[name] = styled(icon, {
    shouldForwardProp: (prop) => prop !== 'customColor' && prop !== 'size',
  })<{ customColor?: string; size?: string }>`
    ${({ theme, customColor }) => useColorOrDefault(theme, customColor)}
    ${({ theme, size }) => useFontSizeOrDefault(theme, size)}
  `
})

// Destructure the styled icons object for named exports.
export const {
  BookingIcon,
  CancelledBookingsIcon,
  NoBookingsIcon,
  InfoIcon,
  UmbrellaIcon,
  CloseIcon,
  AddCartIcon,
  RemoveCartIcon,
  AddIcon,
  RemoveIcon,
  AnalyticsIcon,
  CustomersIcon,
  SettingsIcon,
  AddCardIcon,
  EditCalendarIcon,
  PricingIcon,
  DeleteIcon,
  EmailIcon,
  RefundIcon,
  NumbersIcon,
  PeopleAltIcon,
  EuroIcon,
} = styledIcons
